/* eslint-disable react/jsx-no-bind */
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {hot} from 'react-hot-loader';
import {Switch, Route, withRouter} from 'react-router-dom';
import {withApollo} from 'react-apollo';
import {autobind} from 'core-decorators';
import {enableSessionOrganizationChange} from 'core/utils';
import {getAnonymousUrls, getAuthenticatedUrls} from 'core/urls';
import userQuery from 'graphql/queries/user.gql';
import PageTitle from 'components/page-title';
import resetSessionOrganizationMutation from 'graphql/mutations/users/reset-session-organization.gql';
import changeSessionOrganizationMutation from 'graphql/mutations/users/change-session-organization.gql';
import Footer from 'components/footer';
import Header from 'components/header';
import Spinner from 'components/spinner';
import SessionOrganizationTutorial from 'components/session-organization-tutorial';
import InitialOrganizationSelector from 'components/initial-organization-selector';
import TermsAndConditionsPopup from 'components/terms-and-conditions-popup';
import styles from './styles.css';

@hot(module)
@withApollo
@withRouter
@autobind
export default class App extends Component {
    // Note: user queries and session organization mutations have to be performed ad-hoc and synchronously
    // to avoid request parallelization causing race conditions and inconsistencies between session values.
    // In order to maintain app re-rendering when user query changes we need to manually create
    // an observable and a subscription.
    static contextTypes = {
        router: PropTypes.object,
    };

    state = {
        user: undefined,
        loading: true,
        showTermsAndConditions: false,
    }

    constructor(props, context) {
        super(props, context);
        this.userQueryObservable = this.props.client.watchQuery({query: userQuery});
    }

    componentDidMount() {
        this.initialize();
    }

    componentWillReceiveProps() {
        window.scroll({top: 0, left: 0});
    }

    componentWillUnmount() {
        this.userQuerySubscription.unsubscribe();
    }

    async fetchUser() {
        const response = await this.userQueryObservable.refetch();
        return response.data.user;
    }

    async initialize() {
        this.setState({loading: true});

        await this.props.client.mutate({mutation: resetSessionOrganizationMutation});

        this.userQuerySubscription = this.userQueryObservable.subscribe({
            next: response => this.setState({user: response.data.user}),
            error: error => {
                throw error;
            },
        });

        const user = await this.fetchUser();

        this.setState({user, loading: false});
    }

    async handleSessionOrganizationChange(id) {
        this.setState({loading: true});

        await this.props.client.mutate({
            mutation: changeSessionOrganizationMutation,
            variables: {organization: id},
        });
        const user = await this.fetchUser();

        this.setState(
            {user, loading: false},
            () => this.context.router.history.push('/'),
        );
    }

    renderRoute(url) {
        return <Route exact key={url.path} {...url} />;
    }

    renderRoutes() {
        if (this.state.loading) return <Spinner />;
        if (this.state.user) return <Switch>{getAuthenticatedUrls(this.state.user).map(this.renderRoute)}</Switch>;
        return <Switch>{getAnonymousUrls().map(this.renderRoute)}</Switch>;
    }

    renderTermsAndConditionsPopup() {
        if (this.state.showTermsAndConditions) return <TermsAndConditionsPopup />;
    }

    renderSessionOrganizationTutorial() {
        if (enableSessionOrganizationChange(this.state.user)) return <SessionOrganizationTutorial />;
    }

    renderPageTitle() {
        if ((this.context.router.route.location.pathname !== '/') &&
            (this.context.router.route.location.pathname !== '/pedidos/') &&
            (this.context.router.route.location.pathname !== '/creditos/')) {
            return <PageTitle pathname={this.context.router.route.location.pathname} />;
        }
    }

    renderInitialOrganizationSelector() {
        if (enableSessionOrganizationChange(this.state.user) && !this.state.user.organization.createDealerOrders) {
            return <InitialOrganizationSelector onSessionOrganizationChange={this.handleSessionOrganizationChange} />;
        }
    }

    render() {
        return (
            <div className={styles.root}>
                <Header
                    user={this.state.user}
                    onSessionOrganizationChange={
                        enableSessionOrganizationChange(this.state.user)
                            ? this.handleSessionOrganizationChange
                            : undefined
                    }
                />
                {this.renderPageTitle()}
                <div className={styles.container}>
                    {this.renderRoutes()}
                </div>
                <Footer />
                {this.renderTermsAndConditionsPopup()}
                {this.renderInitialOrganizationSelector()}
                {this.renderSessionOrganizationTutorial()}
                <div id='popup' />
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./views/app/index.js